import React, { useState } from 'react';
import { Menu, MenuItem, Paper, Typography } from '@mui/material';
import i18n from '../../i18n';
import { MenuLibraryManagementState, MenuLibraryDisplayLang } from './menu-library-management-state';
import { ReactComponent as FlagChinaIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-china.svg';
import { ReactComponent as FlagUkIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-united-kingdom.svg';
import { ReactComponent as FlagItalyIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-italy.svg';
import { ReactComponent as FlagSpainIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-spain.svg';
import { ReactComponent as FlagFranceIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-france.svg';
import { ReactComponent as FlagGermanyIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-germany.svg';
import { ReactComponent as FlagPortugalIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-portugal.svg';
import { ReactComponent as FlagSloveniaIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-slovenia.svg';
import { ReactComponent as FlagCataloniaIcon } from '../../static/images/flag-svg/emojione-v1_flag-for-catalonia.svg';
import './scss/menu-library-menu-detail.style.scss';
/** 子类别行 variant 依赖 `.menu-library-create-dialog` 祖先下的触发器样式 */
import './scss/menu-library-create-menu-dialog.style.scss';

// ZH EN IT ES FR DE PT SL CA
const LANG_ITEMS: { value: MenuLibraryDisplayLang; label: string }[] = [
    { value: 'unspecified', label: 'menu_library_lang_unspecified' },
    { value: 'zh', label: 'menu_library_lang_zh' },
    { value: 'en', label: 'menu_library_lang_en' },
    { value: 'it', label: 'menu_library_lang_it' },
    { value: 'sp', label: 'menu_library_lang_sp' },
    { value: 'fr', label: 'menu_library_lang_fr' },
    { value: 'de', label: 'menu_library_lang_de' },
    { value: 'pt', label: 'menu_library_lang_pt' },
    { value: 'sl', label: 'menu_library_lang_sl' },
    { value: 'ca', label: 'menu_library_lang_ca' },
];
/** 语言对应界面缩写（与设计稿 ZH / EN 等一致；未指定用 i18n key） */
const LANG_CODE: Record<MenuLibraryDisplayLang, string> = {
    unspecified: 'menu_library_lang_unspecified',
    zh: 'ZH',
    en: 'EN',
    it: 'IT',
    sp: 'ES',
    fr: 'FR',
    de: 'DE',
    pt: 'PT',
    sl: 'SL',
    ca: 'CA',
};

/**
 * 未指定展示语言时的占位图标（虚线框，stroke 使用 currentColor）
 * @param props 透传至根 svg（如 className）
 */
function LangUnspecifiedOutlineIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={40}
            height={40}
            viewBox="0 0 40 40"
            fill="none"
            {...props}
            aria-hidden
        >
            <rect x="7" y="11" width="26" height="18" rx="3" stroke="currentColor" strokeWidth="2" strokeDasharray="5 4" />
        </svg>
    );
}

/** 语言对应国旗图标（未指定无国旗） */
const LANG_FLAG: Record<MenuLibraryDisplayLang, React.FC<React.SVGProps<SVGSVGElement>>> = {
    unspecified: LangUnspecifiedOutlineIcon,
    zh: FlagChinaIcon,
    en: FlagUkIcon,
    it: FlagItalyIcon,
    sp: FlagSpainIcon,
    fr: FlagFranceIcon,
    de: FlagGermanyIcon,
    pt: FlagPortugalIcon,
    sl: FlagSloveniaIcon,
    ca: FlagCataloniaIcon,
};

export type MenuLanguageMenuButtonVariant = 'pill' | 'subcategoryRow';

type Props = {
    /** 菜单库状态（未传受控 `value`/`onChange` 时读写 `menuDisplayLang`） */
    state: MenuLibraryManagementState;
    /** 受控：当前语言（与 `onChange` 成对使用，绑定 `createDialogFirstMenuDisplayLang`，与详情 `menuDisplayLang` 分离） */
    value?: MenuLibraryDisplayLang;
    /** 受控：切换语言 */
    onChange?: (lang: MenuLibraryDisplayLang) => void;
    /**
     * `pill`：详情/新建药丸（默认）。
     * `subcategoryRow`：新建弹窗子类别行等——无边框按钮、`stopPropagation`、独立 aria；样式需在 `.menu-library-create-dialog` 祖先下生效。
     */
    variant?: MenuLanguageMenuButtonVariant;
};

/**
 * 设计稿样式：国旗 + 语言码，点击展开菜单切换展示语言。
 * - **详情顶栏**：只传 `state`，使用 `menuDisplayLang`（菜品列表展示名）。
 * - **新建弹窗一级菜单名称旁**：传入 `value` + `onChange`，绑定 `createDialogFirstMenuDisplayLang`。
 *
 * 业务上与 RBP2 `first-menu-detail` 的 `BaseTextField` 语言选择（`selectedI18nName`、`getI18nOptions`）对应：`unspecified`≈`default`，`zh|en|it|sp`≈`ZH|EN|IT|ES`。
 */
export function MenuLanguageMenuButton(props: Props) {
    const { state, value, onChange, variant = 'pill' } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const controlled = onChange != null;
    const lang = controlled ? (value ?? 'unspecified') : state.menuDisplayLang;
    const FlagIcon = LANG_FLAG[lang];
    const open = Boolean(anchorEl);
    const isSubRow = variant === 'subcategoryRow';

    const openMenu = (e: React.MouseEvent<HTMLElement>) => {
        if (isSubRow) {
            e.stopPropagation();
        }
        setAnchorEl(e.currentTarget);
    };

    const applyLang = (next: MenuLibraryDisplayLang, e?: React.MouseEvent) => {
        if (isSubRow && e) {
            e.stopPropagation();
        }
        if (onChange) {
            onChange(next);
        } else {
            state.setMenuDisplayLang(next);
        }
        setAnchorEl(null);
    };

    const ariaLabel = i18n.t(
        isSubRow ? 'menu_library_create_dialog_subcategory_col_lang' : 'menu_library_detail_menu_language',
    );

    const menuPaperClass = isSubRow
        ? 'menu-library-create-dialog__sub-lang-menu-paper'
        : 'menu-library-menu-detail__lang-menu-paper';
    const menuItemClass = isSubRow
        ? 'menu-library-create-dialog__sub-lang-menu-item'
        : 'menu-library-menu-detail__lang-menu-item';
    const menuCodeClass = isSubRow
        ? 'menu-library-create-dialog__sub-lang-menu-code'
        : 'menu-library-menu-detail__lang-menu-code';
    const flagMenuClass = isSubRow
        ? 'menu-library-create-dialog__sub-lang-flag menu-library-create-dialog__sub-lang-flag--menu'
        : 'menu-library-menu-detail__lang-flag menu-library-menu-detail__lang-flag--menu';

    const menu = (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ className: menuPaperClass }}
        >
            {LANG_ITEMS.flatMap((item) => {
                const F = LANG_FLAG[item.value];
                return (
                    <MenuItem
                        key={item.value}
                        selected={lang === item.value}
                        onClick={(e) => applyLang(item.value, e)}
                        className={menuItemClass}
                    >
                        {item.value !== 'unspecified' && <F className={flagMenuClass} aria-hidden />}
                        <Typography variant="subtitle2" color="text.light" className={menuCodeClass}>
                            {i18n.t(LANG_CODE[item.value])}
                        </Typography>
                    </MenuItem>
                );
            })}
        </Menu>
    );

    if (isSubRow) {
        return (
            <>
                <button
                    type="button"
                    className="menu-library-create-dialog__sub-lang-trigger"
                    onClick={openMenu}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    aria-label={ariaLabel}
                >
                    {lang !== 'unspecified' && (
                        <FlagIcon
                            className="menu-library-create-dialog__sub-lang-flag menu-library-create-dialog__sub-lang-flag--trigger"
                            aria-hidden
                        />
                    )}
                    <Typography component="span" variant="body2" className="menu-library-create-dialog__sub-lang-code">
                        {i18n.t(LANG_CODE[lang])}
                    </Typography>
                </button>
                {menu}
            </>
        );
    }

    return (
        <>
            <Paper
                variant="outlined"
                component="button"
                type="button"
                className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--clickable menu-library-menu-detail__price-pill--lang-menu"
                onClick={openMenu}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                aria-label={ariaLabel}
            >
                {lang !== 'unspecified' && (
                    <FlagIcon
                        className="menu-library-menu-detail__lang-flag menu-library-menu-detail__lang-flag--trigger"
                        aria-hidden
                    />
                )}
                <Typography component="span" variant="body1" className="menu-library-menu-detail__price-pill-text  ">
                    {i18n.t(LANG_CODE[lang])}
                </Typography>
            </Paper>
            {menu}
        </>
    );
}
